import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { MarketingCharacterAsset, MarketingVideoRecord } from './types';
import type { MarketingSocialAsset } from './marketingWorkspaceState';
import { serializeSocialAsset } from './socialContent';

const { Paragraph, Text, Title } = Typography;
type AssetFilter = 'all' | 'video' | 'character' | 'social';
type AssetPreview =
  | { kind: 'video'; title: string; src: string; downloadUrl?: string }
  | { kind: 'portrait'; title: string; src: string; asset: MarketingCharacterAsset }
  | { kind: 'social'; title: string; asset: MarketingSocialAsset };

const formatAssetLabel = (value?: string | null, fallback = 'Marketing video'): string => {
  const normalized = String(value || '').trim().replace(/[_-]+/g, ' ');
  if (!normalized) return fallback;
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const VIDEO_MODE_LABELS: Record<string, string> = {
  cover_animate: 'Animated cover',
  image_to_video: 'Image to video',
  launch_teaser: 'Launch teaser',
  promo_clip: 'Promotional clip',
  text_to_video: 'Text to video',
};

const formatVideoLabel = (mode?: string | null): string => (
  VIDEO_MODE_LABELS[String(mode || '').trim().toLowerCase()] || formatAssetLabel(mode)
);

interface MarketingAssetsWorkspaceProps {
  apiBase: string;
  bookId: string;
  userId: string;
  editionLanguage: string;
  socialAssets: MarketingSocialAsset[];
  onDeleteSocialAsset: (assetId: string) => void;
  onCreateAsset: () => void;
  initialFilter?: AssetFilter;
}

const MarketingAssetsWorkspace: React.FC<MarketingAssetsWorkspaceProps> = ({
  apiBase,
  bookId,
  userId,
  editionLanguage,
  socialAssets,
  onDeleteSocialAsset,
  onCreateAsset,
  initialFilter = 'all',
}) => {
  const [filter, setFilter] = useState<AssetFilter>(initialFilter);
  const [search, setSearch] = useState('');
  const [videos, setVideos] = useState<MarketingVideoRecord[]>([]);
  const [portraits, setPortraits] = useState<MarketingCharacterAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<AssetPreview | null>(null);

  useEffect(() => setFilter(initialFilter), [initialFilter]);

  const loadAssets = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const videoParams = new URLSearchParams({ userId, page: '1', pageSize: '100', language: editionLanguage });
      const characterParams = new URLSearchParams({ userId });
      const [videoResult, portraitResult] = await Promise.allSettled([
        fetch(`${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos?${videoParams.toString()}`),
        fetch(`${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets?${characterParams.toString()}`),
      ]);
      if (videoResult.status === 'fulfilled' && videoResult.value.ok) {
        const payload = await videoResult.value.json();
        setVideos(Array.isArray(payload?.items) ? payload.items : []);
      }
      if (portraitResult.status === 'fulfilled' && portraitResult.value.ok) {
        const payload = await portraitResult.value.json();
        setPortraits(Array.isArray(payload?.items) ? payload.items : []);
      }
    } catch (error) {
      console.error('Failed to load marketing assets', error);
      message.error('Some assets could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, bookId, editionLanguage, userId]);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  const normalizedSearch = search.trim().toLowerCase();
  const visibleVideos = useMemo(() => videos.filter(video => !normalizedSearch || [video.prompt, video.mode, video.model].join(' ').toLowerCase().includes(normalizedSearch)), [normalizedSearch, videos]);
  const visiblePortraits = useMemo(() => portraits.filter(asset => !normalizedSearch || [asset.characterName, asset.prompt, asset.stylePreset].join(' ').toLowerCase().includes(normalizedSearch)), [normalizedSearch, portraits]);
  const visibleSocial = useMemo(() => socialAssets.filter(asset => !normalizedSearch || [asset.title, asset.caption, asset.format].join(' ').toLowerCase().includes(normalizedSearch)), [normalizedSearch, socialAssets]);

  const copySocial = async (asset: MarketingSocialAsset) => {
    await navigator.clipboard.writeText(serializeSocialAsset(asset));
    message.success('Content pack copied.');
  };

  const downloadSocial = (asset: MarketingSocialAsset) => {
    const blob = new Blob([serializeSocialAsset(asset)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${asset.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'social-content'}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const downloadPortrait = async (asset: MarketingCharacterAsset) => {
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets/${encodeURIComponent(asset.id)}/download`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            filename: asset.downloadFilename || `${asset.characterName || 'character'}.png`,
          }),
        }
      );
      if (!response.ok) throw new Error('Failed to create download link.');
      const payload = await response.json();
      const signedUrl = String(payload?.signedUrl || '').trim();
      if (!signedUrl) throw new Error('Missing download link.');
      const anchor = document.createElement('a');
      anchor.href = signedUrl;
      anchor.target = '_blank';
      anchor.rel = 'noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error: any) {
      message.error(error?.message || 'Failed to download character image.');
    }
  };

  const deleteVideo = async (video: MarketingVideoRecord) => {
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/${encodeURIComponent(video.id)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!response.ok) throw new Error('Failed to delete video.');
      setVideos(current => current.filter(item => item.id !== video.id));
      setPreview(null);
      message.success('Video deleted.');
    } catch (error: any) {
      message.error(error?.message || 'Failed to delete video.');
    }
  };

  const deletePortrait = async (asset: MarketingCharacterAsset) => {
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets/${encodeURIComponent(asset.id)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!response.ok) throw new Error('Failed to delete character image.');
      setPortraits(current => current.filter(item => item.id !== asset.id));
      setPreview(null);
      message.success('Character image deleted.');
    } catch (error: any) {
      message.error(error?.message || 'Failed to delete character image.');
    }
  };

  const showVideos = filter === 'all' || filter === 'video';
  const showPortraits = filter === 'all' || filter === 'character';
  const showSocial = filter === 'all' || filter === 'social';
  const visibleCount = (showVideos ? visibleVideos.length : 0) + (showPortraits ? visiblePortraits.length : 0) + (showSocial ? visibleSocial.length : 0);

  return (
    <div className="marketing-assets-workspace">
      <section className="marketing-workspace-page-header">
        <div>
          <span className="marketing-workspace__kicker">Generated and saved output</span>
          <Title level={3}>Assets</Title>
          <Paragraph>Find every video, portrait, and social pack in one place.</Paragraph>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void loadAssets()}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateAsset}>Create asset</Button>
        </Space>
      </section>
      <div className="marketing-assets-toolbar">
        <Segmented
          value={filter}
          onChange={value => setFilter(value as AssetFilter)}
          options={[
            { value: 'all', label: `All (${videos.length + portraits.length + socialAssets.length})` },
            { value: 'video', label: `Videos (${videos.length})` },
            { value: 'character', label: `Portraits (${portraits.length})` },
            { value: 'social', label: `Social (${socialAssets.length})` },
          ]}
        />
        <Input.Search allowClear value={search} onChange={event => setSearch(event.target.value)} placeholder="Search assets" />
      </div>
      <Spin spinning={loading}>
        {visibleCount ? (
          <div className="marketing-unified-asset-grid">
            {showVideos ? visibleVideos.map(video => {
              const streamParams = new URLSearchParams({ userId });
              const streamUrl = video.latestHistoryId
                ? `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/history/${encodeURIComponent(video.latestHistoryId)}/stream?${streamParams.toString()}`
                : '';
              const status = String(video.status || '').trim().toLowerCase();
              const failed = ['failed', 'error', 'cancelled', 'canceled'].includes(status);
              const ready = ['completed', 'complete', 'ready', 'saved', 'succeeded', 'success'].includes(status);
              const previewUrl = streamUrl || String(video.signedUrl || '');
              const hasOutput = !failed && Boolean(previewUrl) && (ready || !status);
              const assetLabel = formatVideoLabel(video.mode);
              const downloadUrl = streamUrl ? `${streamUrl}&download=1` : String(video.signedUrl || '');
              return (
                <article className={`marketing-unified-asset-card${failed ? ' marketing-unified-asset-card--failed' : ''}`} key={`video-${video.id}`}>
                  {failed ? (
                    <div className="marketing-unified-asset-card__preview marketing-unified-asset-card__preview--failed" aria-label={`Failed video ${assetLabel}`}>
                      <ExclamationCircleOutlined />
                      <strong>Generation failed</strong>
                      <span>No video was created.</span>
                    </div>
                  ) : hasOutput ? (
                    <button
                      type="button"
                      className="marketing-unified-asset-card__preview marketing-unified-asset-card__preview-button marketing-unified-asset-card__preview--video"
                      aria-label={`Preview video ${assetLabel}`}
                      onClick={() => setPreview({ kind: 'video', title: assetLabel, src: previewUrl, downloadUrl })}
                    >
                      <video muted preload="metadata" src={previewUrl} />
                      <span className="marketing-unified-asset-card__preview-label"><EyeOutlined /> Preview</span>
                    </button>
                  ) : (
                    <div className="marketing-unified-asset-card__preview marketing-unified-asset-card__preview--pending">
                      <Spin size="small" spinning={['queued', 'running', 'processing', 'submitted'].includes(status)} />
                      <strong>{status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'Processing'}</strong>
                      <span>Video output is not ready yet.</span>
                    </div>
                  )}
                  <Space wrap size={5}>
                    <Tag color="blue">Video</Tag>
                    <Tag>{video.language || editionLanguage}</Tag>
                    {failed ? <Tag color="error">Failed</Tag> : null}
                    {!failed && !ready && status ? <Tag color="gold">{formatAssetLabel(status)}</Tag> : null}
                  </Space>
                  <Text>{assetLabel}</Text>
                  <Text type="secondary">{video.durationSeconds || '?'} seconds · {video.aspectRatio || 'Original ratio'}</Text>
                  {failed && video.error ? <Text className="marketing-unified-asset-card__error" title={video.error}>{video.error}</Text> : null}
                  <div className="marketing-unified-asset-card__actions">
                    <Space wrap>
                      {hasOutput && downloadUrl ? <Button icon={<DownloadOutlined />} href={downloadUrl}>Download</Button> : null}
                      <Popconfirm title="Delete this video record?" onConfirm={() => void deleteVideo(video)}>
                        <Button danger icon={<DeleteOutlined />} aria-label={`Delete video ${assetLabel}`} />
                      </Popconfirm>
                    </Space>
                  </div>
                </article>
              );
            }) : null}
            {showPortraits ? visiblePortraits.map(asset => {
              const title = asset.characterName || 'Character portrait';
              const thumbnailUrl = asset.previewStreamUrl || asset.previewUrl || asset.imageStreamUrl || asset.imageUrl || '';
              const fullImageUrl = asset.imageStreamUrl || asset.imageUrl || thumbnailUrl;
              return (
              <article className="marketing-unified-asset-card" key={`character-${asset.id}`}>
                {thumbnailUrl ? (
                  <button
                    type="button"
                    className="marketing-unified-asset-card__preview marketing-unified-asset-card__preview-button"
                    aria-label={`Preview portrait ${title}`}
                    onClick={() => setPreview({ kind: 'portrait', title, src: fullImageUrl, asset })}
                  >
                    <img src={thumbnailUrl} alt={title} />
                    <span className="marketing-unified-asset-card__preview-label"><EyeOutlined /> Preview</span>
                  </button>
                ) : (
                  <div className="marketing-unified-asset-card__preview">
                    <div className="marketing-unified-asset-card__placeholder">No preview</div>
                  </div>
                )}
                <Space wrap size={5}><Tag color="purple">Portrait</Tag></Space>
                <Text strong>{title}</Text>
                <Text type="secondary">{formatAssetLabel(asset.stylePreset || asset.visualStyle, 'Character image')}</Text>
                <div className="marketing-unified-asset-card__actions">
                  <Space wrap>
                    <Button icon={<DownloadOutlined />} onClick={() => void downloadPortrait(asset)}>Download</Button>
                    <Popconfirm title="Delete this portrait?" onConfirm={() => void deletePortrait(asset)}>
                      <Button danger icon={<DeleteOutlined />} aria-label={`Delete portrait ${title}`} />
                    </Popconfirm>
                  </Space>
                </div>
              </article>
            );}) : null}
            {showSocial ? visibleSocial.map(asset => (
              <article className="marketing-unified-asset-card" key={`social-${asset.id}`}>
                <button
                  type="button"
                  className="marketing-unified-asset-card__preview marketing-unified-asset-card__preview-button marketing-unified-asset-card__preview--social"
                  aria-label={`Preview social asset ${asset.title}`}
                  onClick={() => setPreview({ kind: 'social', title: asset.title, asset })}
                >
                  <span>{asset.cards[0]?.text || asset.title}</span>
                  <span className="marketing-unified-asset-card__preview-label"><EyeOutlined /> Preview all</span>
                </button>
                <Space wrap size={5}><Tag color="gold">Social</Tag><Tag>{formatAssetLabel(asset.format)}</Tag></Space>
                <Text strong>{asset.title}</Text>
                <Text type="secondary">{asset.cards.length} card{asset.cards.length === 1 ? '' : 's'} · {asset.editionLanguage}</Text>
                <div className="marketing-unified-asset-card__actions">
                  <Space wrap>
                    <Button icon={<CopyOutlined />} onClick={() => void copySocial(asset)}>Copy</Button>
                    <Button icon={<DownloadOutlined />} onClick={() => downloadSocial(asset)}>Download</Button>
                    <Popconfirm title="Delete this saved social pack?" onConfirm={() => onDeleteSocialAsset(asset.id)}>
                      <Button danger icon={<DeleteOutlined />} aria-label={`Delete ${asset.title}`} />
                    </Popconfirm>
                  </Space>
                </div>
              </article>
            )) : null}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching assets yet.">
            <Button type="primary" onClick={onCreateAsset}>Create your first asset</Button>
          </Empty>
        )}
      </Spin>
      <Modal
        title={preview?.kind === 'video' ? 'Video preview' : preview?.kind === 'portrait' ? 'Portrait preview' : preview?.kind === 'social' ? 'Social preview' : 'Asset preview'}
        open={Boolean(preview)}
        onCancel={() => setPreview(null)}
        width="min(900px, calc(100vw - 32px))"
        footer={preview ? [
          preview.kind === 'video' && preview.downloadUrl ? <Button key="download-video" icon={<DownloadOutlined />} href={preview.downloadUrl}>Download</Button> : null,
          preview.kind === 'portrait' ? <Button key="download-portrait" icon={<DownloadOutlined />} onClick={() => void downloadPortrait(preview.asset)}>Download</Button> : null,
          preview.kind === 'social' ? <Button key="copy-social" icon={<CopyOutlined />} onClick={() => void copySocial(preview.asset)}>Copy</Button> : null,
          preview.kind === 'social' ? <Button key="download-social" icon={<DownloadOutlined />} onClick={() => downloadSocial(preview.asset)}>Download</Button> : null,
          <Button key="close" type="primary" onClick={() => setPreview(null)}>Close</Button>,
        ].filter(Boolean) : null}
        destroyOnClose
      >
        {preview?.kind === 'video' ? <video className="marketing-asset-preview-modal__media" controls autoPlay src={preview.src} /> : null}
        {preview?.kind === 'portrait' ? <img className="marketing-asset-preview-modal__media" src={preview.src} alt={preview.title} /> : null}
        {preview?.kind === 'social' ? (
          <div className="marketing-asset-preview-modal__social">
            <Paragraph>{preview.asset.caption}</Paragraph>
            <div className="marketing-asset-preview-modal__cards">
              {preview.asset.cards.map((card, index) => (
                <div key={`${preview.asset.id}-${index}`}>
                  <Text type="secondary">Slide {index + 1}</Text>
                  <strong>{card.text}</strong>
                  <span>{card.role.charAt(0).toUpperCase()}{card.role.slice(1)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default MarketingAssetsWorkspace;
